import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ChevronDown } from 'lucide-react';
import { BrandLogo } from '../components/brand/BrandLogo';

/*
 * LoyalPass home page.
 *
 * What we sell: the merchant's own loyalty card, in their customers' phone
 * (Apple Wallet / Google Wallet), stamped with the shop's phone camera.
 * The product is the visual: real cards of real LoyalPass merchants (shared
 * with their agreement, cropped without QR code).
 *
 * One orchestrated moment only: on load, the cards slide into the Wallet,
 * then the customer receives the pass-update notification (the real
 * Apple Wallet behaviour when a stamp is added). Everything else is static.
 */

export const CARDS = {
  shaneliss: { src: '/landing/carte-shaneliss.webp', name: 'Shaneliss Paris', kind: 'Institut de beauté' },
  epices: { src: '/landing/carte-epices-et-tout.webp', name: 'Épices & Tout', kind: 'Épicerie' },
  crepmoi: { src: '/landing/carte-crep-moi.webp', name: 'Crêp’ Moi', kind: 'Food truck' },
  kepasta: { src: '/landing/carte-kepasta.webp', name: 'Kepasta', kind: 'Restaurant' },
};
const STACK = [CARDS.shaneliss, CARDS.epices, CARDS.crepmoi, CARDS.kepasta]; // back → front
const SHOWCASE = [CARDS.kepasta, CARDS.crepmoi, CARDS.epices, CARDS.shaneliss];

export const STEPS = [
  {
    img: '/landing/ecran-inscription.webp',
    title: 'Le client scanne le QR de votre boutique',
    text: 'Un prénom, un email, et il découvre votre carte et sa récompense.',
  },
  {
    img: '/landing/ecran-carte.webp',
    title: 'Votre carte arrive dans son téléphone',
    text: 'Apple Wallet ou Google Wallet. Rien à télécharger, rien à perdre.',
  },
  {
    img: '/landing/ecran-scan.webp',
    title: 'Vous tamponnez avec l’appareil photo',
    text: 'Le téléphone de la caisse suffit. Un appui, et sa carte se met à jour toute seule.',
  },
];

const BENEFITS = [
  { title: 'Plus de carte perdue ni oubliée', text: 'Elle est dans le téléphone de vos clients, à côté de leur carte bancaire.' },
  { title: 'Ils pensent à revenir', text: 'À chaque tampon, ils reçoivent une notification : « Plus que 2 tampons avant votre cadeau ».' },
  { title: 'Vous voyez qui revient', text: 'Clients revenus, passages, récompenses : tout est dans votre tableau de bord.' },
];

export const PLANS = [
  {
    name: 'Essentiel', price: 29, popular: false,
    features: ['1 carte de fidélité', '1 000 clients', 'Apple Wallet + Google Wallet', 'Scans illimités', 'Toute l’équipe scanne avec le même compte', 'Statistiques', 'Notifications push', 'Géofencing 1 zone', 'Support email (lun-ven, <48h)'],
  },
  {
    name: 'Pro', price: 59, popular: true,
    features: ['3 cartes de fidélité', '5 000 clients', 'Tout Essentiel +', 'Géofencing jusqu’à 10 zones', 'Support prioritaire (<24h ouvrées)'],
  },
];

const FAQS = [
  { q: 'Mes clients doivent-ils télécharger une app ?', a: 'Non. La carte se range dans Apple Wallet ou Google Wallet, déjà présents sur leur téléphone.' },
  { q: 'Combien de temps pour créer ma carte ?', a: 'Environ 5 minutes : vos couleurs, votre logo, une photo, le nombre de tampons ou de points, et la récompense. Vos clients peuvent s’inscrire tout de suite après.' },
  { q: 'Et si mon client n’a pas d’iPhone ?', a: 'La carte fonctionne aussi avec Google Wallet sur Android.' },
  { q: 'Faut-il acheter un appareil pour scanner ?', a: 'Non. Vous scannez la carte du client avec l’appareil photo de votre téléphone, comme n’importe quel QR code. Toute l’équipe peut le faire avec le même compte.' },
  { q: 'Et mes clients qui ont déjà une carte papier ?', a: 'Affichez le QR de votre boutique en caisse : ils s’inscrivent en le scannant, et vous pouvez leur reporter leurs tampons depuis leur fiche.' },
  { q: 'Puis-je arrêter à tout moment ?', a: 'Oui, c’est sans engagement. Un simple message suffit, et l’abonnement s’arrête à la fin de la période en cours.' },
  { q: 'Mes données sont-elles protégées ?', a: 'Les données sont hébergées en Europe, conformément au RGPD. Nous ne revendons aucune donnée.' },
];

export const CSS = `
.lp { --ink:#0F1115; --muted:#5B616B; --line:#E3E6EA; --page:#F4F5F7; --brand:#0B0D12; --brand-ink:#000000; }
.lp-display { letter-spacing: -0.045em; }
@keyframes lp-card-in { from { transform: translateY(48px) scale(.94); opacity: 0; } to { transform: none; opacity: 1; } }
@keyframes lp-notif-in {
  0% { transform: translateY(-140%); opacity: 0; }
  65% { transform: translateY(6%); opacity: 1; }
  100% { transform: none; opacity: 1; }
}
@keyframes lp-stamp { 0% { transform: scale(1); } 40% { transform: scale(1.18); } 100% { transform: scale(1); } }
.lp-card-in { animation: lp-card-in .75s cubic-bezier(.2,.8,.2,1) both; }
.lp-notif-in { animation: lp-notif-in .7s cubic-bezier(.2,.9,.25,1.1) 2.1s both; }
.lp-stamp { animation: lp-stamp .5s ease-out 2.25s both; }
.lp-lift { transition: transform .25s ease, box-shadow .25s ease; }
.lp-lift:hover { transform: translateY(-4px); box-shadow: 0 26px 46px -22px rgba(15,17,21,.55); }
.lp-snap { scroll-snap-type: x mandatory; scrollbar-width: none; }
.lp-snap::-webkit-scrollbar { display: none; }
.lp-snap > * { scroll-snap-align: center; }
@media (prefers-reduced-motion: reduce) {
  .lp-card-in, .lp-notif-in, .lp-stamp { animation: none; }
  .lp-lift { transition: none; }
}
`;

/* ── iPhone with the Wallet stack and the pass-update notification ── */
export const PhoneWallet = () => (
  <div className="relative mx-auto w-[288px] sm:w-[330px] lg:w-[350px]" data-testid="hero-wallet-stack">
    {/* Two loose cards behind the phone (large screens), part of the same entrance */}
    <img src={CARDS.crepmoi.src} alt="" aria-hidden="true" width="720" height="532"
      className="lp-card-in hidden xl:block absolute -left-[130px] top-[54%] w-[220px] rounded-[16px] shadow-[0_26px_50px_-24px_rgba(15,17,21,.55)]"
      style={{ animationDelay: '0.05s', rotate: '-8deg' }} />
    <img src={CARDS.epices.src} alt="" aria-hidden="true" width="720" height="532"
      className="lp-card-in hidden xl:block absolute -right-[120px] top-[14%] w-[220px] rounded-[16px] shadow-[0_26px_50px_-24px_rgba(15,17,21,.55)]"
      style={{ animationDelay: '0.15s', rotate: '7deg' }} />
    <div className="relative rounded-[52px] bg-[#0B0B0D] p-[11px] shadow-[0_40px_80px_-30px_rgba(15,17,21,.6),inset_0_0_0_1.5px_#2A2B30]">
      <div className="relative rounded-[42px] bg-black overflow-hidden" style={{ aspectRatio: '9 / 16.4' }}>
        {/* Dynamic island + status bar */}
        <div className="absolute top-[10px] left-1/2 -translate-x-1/2 w-[92px] h-[26px] rounded-full bg-[#0B0B0D] z-20" />
        <div className="flex justify-between items-center px-7 pt-[14px] text-white text-[13px] font-semibold">
          <span>9:41</span>
          <span className="flex items-center gap-1" aria-hidden="true">
            <span className="w-[16px] h-[10px] rounded-[2px] border border-white/80 relative">
              <span className="absolute inset-[1.5px] right-[4px] bg-white rounded-[1px]" />
            </span>
          </span>
        </div>
        <p className="text-white text-[26px] font-bold px-5 mt-5" style={{ fontFamily: '-apple-system, system-ui, sans-serif' }}>Wallet</p>

        {/* Cards, back to front — headers peek out like in Apple Wallet */}
        <div className="relative mx-3 mt-4">
          {STACK.map((c, i) => (
            <img
              key={c.src}
              src={c.src}
              alt={i === STACK.length - 1 ? `Carte de fidélité ${c.name} dans Apple Wallet` : ''}
              width="720" height="532"
              className="lp-card-in absolute left-0 w-full rounded-[14px] shadow-[0_-6px_18px_-8px_rgba(0,0,0,.7)]"
              style={{ top: `${i * 46}px`, animationDelay: `${0.25 + i * 0.17}s` }}
            />
          ))}
          <div style={{ height: `${(STACK.length - 1) * 46}px`, aspectRatio: 'auto' }} />
          <div style={{ aspectRatio: '720 / 532' }} />
        </div>

        {/* Pass update notification (real Apple Wallet behaviour on each stamp) */}
        <div className="lp-notif-in absolute top-[46px] left-[10px] right-[10px] z-30 rounded-[20px] bg-[#F5F5F7]/90 backdrop-blur-xl px-3 py-2.5 flex gap-2.5 shadow-[0_10px_30px_-10px_rgba(0,0,0,.6)]" role="img" aria-label="Notification : Kepasta Fidélité, plus que 4 tampons pour un plat offert">
          <img src="/landing/logo-kepasta.webp" alt="" className="lp-stamp w-9 h-9 rounded-[9px] shrink-0 bg-white" width="120" height="120" />
          <div className="min-w-0 flex-1 text-[#111]" style={{ fontFamily: '-apple-system, system-ui, sans-serif' }}>
            <p className="flex justify-between text-[13px] font-semibold leading-tight">
              <span className="truncate">Kepasta Fidélité</span>
              <span className="text-[12px] font-normal text-[#6E6E73] shrink-0 ml-2">maintenant</span>
            </p>
            <p className="text-[13px] leading-snug mt-0.5">Plus que 4 Tampons pour Un Plat Offert</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const SmallPhone = ({ src, alt }) => (
  <div className="rounded-[34px] bg-[#0B0B0D] p-[7px] shadow-[0_24px_50px_-26px_rgba(15,17,21,.55)] w-[220px] sm:w-[240px] mx-auto">
    <img src={src} alt={alt} width="540" height="1168" loading="lazy" className="rounded-[28px] w-full block bg-white" />
  </div>
);

export const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[var(--line)] last:border-0">
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between py-5 text-left gap-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--brand)] rounded"
        data-testid={`faq-${q.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
      >
        <span className="text-[16px] font-semibold text-[var(--ink)]">{q}</span>
        <ChevronDown className={`w-5 h-5 text-[var(--muted)] flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="pb-5 text-[15px] text-[var(--muted)] leading-relaxed -mt-1 max-w-2xl">{a}</p>}
    </div>
  );
};

const PrimaryCta = ({ children, testId, className = '' }) => (
  <Link
    to="/register"
    className={`inline-flex items-center justify-center gap-2 h-14 px-7 whitespace-nowrap rounded-2xl bg-[var(--brand)] hover:bg-[var(--brand-ink)] text-white text-[17px] font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--brand)] ${className}`}
    data-testid={testId}
  >
    {children} <ArrowRight className="w-5 h-5" />
  </Link>
);

const Logo = ({ light = false, collapse = false }) => <BrandLogo tone={light ? 'light' : 'dark'} collapse={collapse} />;

const LandingPage = () => (
  <div className="lp min-h-screen bg-white text-[var(--ink)]">
    <style>{CSS}</style>

    {/* ═══ HEADER ═══ */}
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-md border-b border-[var(--line)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Logo collapse />
        <nav className="flex items-center gap-2 sm:gap-4">
          <a href="#tarifs" className="hidden sm:inline text-[15px] font-medium text-[var(--muted)] hover:text-[var(--ink)]">Tarifs</a>
          <Link to="/login" className="whitespace-nowrap text-[15px] font-medium text-[var(--ink)] px-2 sm:px-3 py-2" data-testid="header-login-btn">Connexion</Link>
          <Link to="/register" className="whitespace-nowrap text-[15px] font-semibold text-white bg-[var(--ink)] hover:bg-black rounded-xl px-3.5 sm:px-4 py-2.5" data-testid="header-signup-btn">Essai gratuit</Link>
        </nav>
      </div>
    </header>

    {/* ═══ HERO ═══ */}
    <section className="bg-[var(--page)] overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 pb-16 lg:py-20 grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-8 items-center">
        <div>
          <h1 className="lp-display text-[42px] leading-[1.02] sm:text-[60px] lg:text-[66px] font-semibold" data-testid="hero-title">
            Votre carte de fidélité, dans le téléphone de vos clients.
          </h1>
          <p className="mt-6 text-[18px] sm:text-[19px] leading-relaxed text-[var(--muted)] max-w-[34ch]">
            À vos couleurs, dans Apple Wallet et Google Wallet. Vous tamponnez avec l’appareil photo de votre téléphone, ils reçoivent une notification à chaque passage.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
            <PrimaryCta testId="hero-start-btn">Créer ma carte gratuitement</PrimaryCta>
            <a href="#comment-ca-marche" className="whitespace-nowrap text-[16px] font-semibold text-[var(--ink)] underline underline-offset-4 decoration-[var(--line)] hover:decoration-[var(--ink)] px-1 py-2" data-testid="hero-how-btn">
              Voir comment ça marche
            </a>
          </div>
          <ul className="mt-7 flex flex-wrap gap-x-5 gap-y-2 text-[15px] text-[var(--muted)]">
            {['14 jours gratuits', 'Sans carte bancaire', 'Sans engagement'].map((t) => (
              <li key={t} className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[var(--ink)]" strokeWidth={2.5} />{t}</li>
            ))}
          </ul>
        </div>
        <PhoneWallet />
      </div>
    </section>

    {/* ═══ PREUVE ═══ */}
    <section className="border-b border-[var(--line)]" data-testid="proof-stat">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-12 grid md:grid-cols-[auto_1fr] gap-4 md:gap-10 items-center">
        <p className="lp-display text-[72px] sm:text-[88px] font-semibold leading-none tabular-nums">84&nbsp;%</p>
        <div>
          <p className="text-[19px] sm:text-[21px] font-semibold leading-snug max-w-[38ch]">
            des clients qui scannent le QR de la boutique ajoutent la carte à leur téléphone.
          </p>
          <p className="text-[14px] text-[var(--muted)] mt-2">
            Mesuré chez les commerces LoyalPass sur les 90 derniers jours : Kepasta, Épices & Tout, Crêp’ Moi, Shaneliss Paris, Mestora…
          </p>
        </div>
      </div>
    </section>

    {/* ═══ COMMENT ÇA MARCHE ═══ */}
    <section id="comment-ca-marche" className="py-16 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <h2 className="lp-display text-[34px] sm:text-[44px] font-semibold leading-tight max-w-[18ch]">Trois gestes, et vos clients reviennent.</h2>
        <ol className="mt-10 sm:mt-14 flex md:grid md:grid-cols-3 gap-8 overflow-x-auto lp-snap -mx-4 px-4 md:mx-0 md:px-0 pb-2">
          {STEPS.map((s, i) => (
            <li key={s.title} className="shrink-0 w-[78%] sm:w-[56%] md:w-auto">
              <SmallPhone src={s.img} alt={s.title} />
              <p className="mt-6 text-[14px] font-semibold text-[var(--brand)] tabular-nums">Étape {i + 1}</p>
              <h3 className="text-[20px] font-bold mt-1 leading-snug">{s.title}</h3>
              <p className="text-[16px] text-[var(--muted)] mt-2 leading-relaxed">{s.text}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>

    {/* ═══ VRAIES CARTES ═══ */}
    <section className="bg-[var(--page)] py-16 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="md:flex md:items-end md:justify-between gap-8">
          <h2 className="lp-display text-[34px] sm:text-[44px] font-semibold leading-tight max-w-[16ch]">Chaque carte à l’image du commerce.</h2>
          <p className="text-[17px] text-[var(--muted)] max-w-[40ch] mt-4 md:mt-0">
            Votre logo, vos couleurs, votre photo, vos tampons. Voici de vraies cartes de commerces LoyalPass.
          </p>
        </div>
        <div className="mt-10 flex md:grid md:grid-cols-2 gap-6 overflow-x-auto lp-snap -mx-4 px-4 md:mx-0 md:px-0 pb-4" data-testid="showcase-cards">
          {SHOWCASE.map((c) => (
            <figure key={c.src} className="shrink-0 w-[86%] sm:w-[60%] md:w-auto">
              <img
                src={c.src} alt={`Carte de fidélité ${c.name}`} width="720" height="532" loading="lazy"
                className="lp-lift w-full rounded-[20px] shadow-[0_18px_36px_-20px_rgba(15,17,21,.5)]"
              />
              <figcaption className="mt-3 text-[15px] text-[var(--muted)]">
                <span className="font-semibold text-[var(--ink)]">{c.name}</span>, {c.kind.toLowerCase()}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>

    {/* ═══ BÉNÉFICES ═══ */}
    <section className="py-16 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-[1fr_1.3fr] gap-10 md:gap-16">
        <h2 className="lp-display text-[34px] sm:text-[44px] font-semibold leading-tight">La carte papier vous coûte des clients.</h2>
        <ul className="divide-y divide-[var(--line)] border-y border-[var(--line)]">
          {BENEFITS.map((b) => (
            <li key={b.title} className="py-6">
              <h3 className="text-[20px] font-bold">{b.title}</h3>
              <p className="text-[16px] text-[var(--muted)] mt-1.5 leading-relaxed">{b.text}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>

    {/* ═══ TÉMOIGNAGE ═══ */}
    <section className="bg-[var(--ink)] text-white py-16 sm:py-24">
      <figure className="max-w-4xl mx-auto px-4 sm:px-6">
        <blockquote className="lp-display text-[28px] sm:text-[40px] font-bold leading-[1.15]" data-testid="testimonial-quote">
          « LoyalPass nous a permis de fidéliser nos clientes facilement, sans qu’elles aient à installer une nouvelle app. »
        </blockquote>
        <figcaption className="mt-6 text-[16px] text-white/70">Mestora, Modestie Luxueuse</figcaption>
      </figure>
    </section>

    {/* ═══ TARIFS ═══ */}
    <section className="py-16 sm:py-24" id="tarifs">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <h2 className="lp-display text-[34px] sm:text-[44px] font-semibold leading-tight">Tarifs simples.</h2>
        <p className="text-[17px] text-[var(--muted)] mt-3" data-testid="pricing-comparison">14 jours gratuits, sans carte bancaire et sans engagement.</p>
        <div className="mt-10 grid md:grid-cols-2 gap-6 max-w-4xl">
          {PLANS.map((plan) => (
            <div
              key={plan.name}
              className={`rounded-[24px] p-7 sm:p-8 flex flex-col ${plan.popular ? 'bg-[var(--ink)] text-white' : 'bg-[var(--page)]'}`}
              data-testid={`pricing-plan-${plan.name.toLowerCase()}`}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-[22px] font-bold">{plan.name}</h3>
                {plan.popular && <span className="text-[13px] font-semibold bg-white/15 rounded-full px-3 py-1">Le plus choisi</span>}
              </div>
              <p className="mt-4 flex items-baseline gap-1.5">
                <span className="lp-display text-[52px] font-semibold leading-none tabular-nums">{plan.price}&nbsp;€</span>
                <span className={plan.popular ? 'text-white/70' : 'text-[var(--muted)]'}>/mois</span>
              </p>
              <ul className="mt-6 space-y-3 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className={`flex items-start gap-2.5 text-[15px] ${plan.popular ? 'text-white/90' : 'text-[var(--ink)]'}`}>
                    <Check className="w-4 h-4 mt-1 shrink-0" strokeWidth={2.5} />{f}
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                className={`mt-8 py-3.5 rounded-2xl text-center text-[16px] font-semibold transition-colors ${
                  plan.popular ? 'bg-white text-[var(--ink)] hover:bg-white/90' : 'bg-[var(--ink)] text-white hover:bg-black'
                }`}
                data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
              >
                Commencer l’essai gratuit
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ═══ FAQ ═══ */}
    <section className="bg-[var(--page)] py-16 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-[1fr_1.6fr] gap-8 md:gap-16">
        <h2 className="lp-display text-[34px] sm:text-[44px] font-semibold leading-tight">Vos questions.</h2>
        <div className="bg-white rounded-[24px] px-6 sm:px-8" data-testid="faq-list">
          {FAQS.map((f) => <FAQItem key={f.q} q={f.q} a={f.a} />)}
        </div>
      </div>
    </section>

    {/* ═══ DERNIER APPEL ═══ */}
    <section className="py-16 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
        <h2 className="lp-display text-[36px] sm:text-[52px] font-semibold leading-[1.05] max-w-[20ch] mx-auto">
          Votre carte peut être prête aujourd’hui.
        </h2>
        <p className="text-[17px] text-[var(--muted)] mt-4">Fabriqué en France, hébergé en Europe, support en français.</p>
        <PrimaryCta testId="cta-start-btn" className="mt-8">Créer ma carte gratuitement</PrimaryCta>
      </div>
    </section>

    {/* ═══ FOOTER ═══ */}
    <footer className="border-t border-[var(--line)] py-10">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-5 text-[14px] text-[var(--muted)]">
        <Logo />
        <nav className="flex flex-wrap justify-center gap-x-5 gap-y-2">
          <Link to="/mentions-legales" className="hover:text-[var(--ink)]">Mentions légales</Link>
          <Link to="/confidentialite" className="hover:text-[var(--ink)]">Confidentialité</Link>
          <Link to="/cgv" className="hover:text-[var(--ink)]">CGV</Link>
          <a href="mailto:contact@getloyalpass.fr" className="hover:text-[var(--ink)]">contact@getloyalpass.fr</a>
        </nav>
        <p>© {new Date().getFullYear()} LoyalPass</p>
      </div>
    </footer>
  </div>
);

export default LandingPage;
